document.documentElement.classList.remove('no-js');

(() => {
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (s, ctx = document) => ctx.querySelector(s);
  const qsa = (s, ctx = document) => [...ctx.querySelectorAll(s)];

/* Product page — carousel + high-variant-safe dropdown rendering */
  const initProduct = pdp => {
    if (pdp.dataset.productReady) return;
    pdp.dataset.productReady = 'true';

    const sectionId = pdp.dataset.sectionId;
    const currentProductUrl = pdp.dataset.productUrl || location.pathname;

    /* Product media carousel */
    const mediaTrack = qs('[data-product-media-track]', pdp);
    const mediaSlides = qsa('[data-media-id]', pdp);
    const mediaThumbs = qsa('[data-media-thumb]', pdp);
    const mediaPosition = qs('[data-media-carousel-position]', pdp);
    let mediaIndex = Math.max(0, mediaSlides.findIndex(slide => slide.classList.contains('is-active')));
    let mediaFrame;

    const setMediaState = index => {
      if (!mediaSlides.length) return;

      mediaIndex = Math.max(0, Math.min(index, mediaSlides.length - 1));

      mediaSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === mediaIndex);
      });

      mediaThumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === mediaIndex;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      if (mediaPosition) {
        mediaPosition.textContent = `${mediaIndex + 1} / ${mediaSlides.length}`;
      }

      mediaThumbs[mediaIndex]?.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    };

    const goToMedia = (index, smooth = true) => {
      if (!mediaTrack || !mediaSlides.length) return;

      const target = mediaSlides[Math.max(0, Math.min(index, mediaSlides.length - 1))];
      if (!target) return;

      mediaTrack.scrollTo({
        left: target.offsetLeft,
        behavior: smooth && !reducedMotion ? 'smooth' : 'auto'
      });

      setMediaState(mediaSlides.indexOf(target));
    };

    qs('[data-media-prev]', pdp)?.addEventListener('click', () => {
      goToMedia((mediaIndex - 1 + mediaSlides.length) % mediaSlides.length);
    });

    qs('[data-media-next]', pdp)?.addEventListener('click', () => {
      goToMedia((mediaIndex + 1) % mediaSlides.length);
    });

    mediaThumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => goToMedia(index));
    });

    mediaTrack?.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToMedia((mediaIndex - 1 + mediaSlides.length) % mediaSlides.length);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToMedia((mediaIndex + 1) % mediaSlides.length);
      }
    });

    mediaTrack?.addEventListener('scroll', () => {
      if (mediaFrame) cancelAnimationFrame(mediaFrame);

      mediaFrame = requestAnimationFrame(() => {
        if (!mediaTrack || !mediaSlides.length) return;

        let closestIndex = 0;
        let closestDistance = Infinity;

        mediaSlides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - mediaTrack.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== mediaIndex) setMediaState(closestIndex);
      });
    }, { passive: true });

    if (mediaSlides.length) {
      requestAnimationFrame(() => goToMedia(mediaIndex, false));
    }

    /* High-variant-safe dropdown picker */
    qsa('[data-option-swatch]', pdp).forEach(swatch => {
      swatch.addEventListener('click', () => {
        const select = document.getElementById(swatch.dataset.selectId);
        if (!select || select.disabled) return;

        select.value = swatch.dataset.valueId || '';
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    qsa('[data-gift-recipient]', pdp).forEach(recipient => {
      const toggle = qs('[data-gift-recipient-toggle]', recipient);
      const fields = qs('[data-gift-recipient-fields]', recipient);
      const email = qs('[data-gift-recipient-email]', recipient);
      const offset = qs('[data-gift-card-offset]', recipient);

      if (offset) offset.value = String(new Date().getTimezoneOffset());

      const syncRecipient = () => {
        const active = Boolean(toggle?.checked);
        if (fields) fields.hidden = !active;
        if (email) email.required = active;
      };

      toggle?.addEventListener('change', syncRecipient);
      syncRecipient();
    });

    const optionGroups = () => qsa('[data-product-option]', pdp);

    const selectedOptionElement = group => {
      const select = qs('[data-option-select]', group);
      return select?.selectedOptions?.[0] || null;
    };

    const selectionIds = (changedOption, changedGroup) => {
      return optionGroups()
        .map(group => {
          if (group === changedGroup) return changedOption?.dataset.optionValueId || '';
          return selectedOptionElement(group)?.dataset.optionValueId || '';
        })
        .filter(Boolean);
    };

    const selectionUrl = (optionElement, ids, includeSection = false) => {
      const productUrl = optionElement?.dataset.productUrl || currentProductUrl;
      const url = new URL(productUrl, location.origin);

      url.searchParams.delete('variant');

      if (ids.length) url.searchParams.set('option_values', ids.join(','));
      else url.searchParams.delete('option_values');

      if (includeSection && sectionId) url.searchParams.set('section_id', sectionId);
      else url.searchParams.delete('section_id');

      return url;
    };

    const announceSelection = nextPdp => {
      const live = qs('[data-product-live-status]', nextPdp);
      if (!live) return;

      const price = qs('[data-product-price]', nextPdp)?.textContent?.trim() || '';
      const stock = qs('[data-product-stock]', nextPdp)?.textContent?.trim() || '';

      setTimeout(() => {
        live.textContent = ['Product selection updated.', price, stock].filter(Boolean).join(' ');
      }, 30);
    };

    optionGroups().forEach(group => {
      const select = qs('[data-option-select]', group);
      if (!select) return;

      select.addEventListener('change', async () => {
        if (select.getAttribute('aria-busy') === 'true') return;

        const selectedOption = select.selectedOptions?.[0];
        if (!selectedOption) return;

        const ids = selectionIds(selectedOption, group);
        const publicUrl = selectionUrl(selectedOption, ids, false);
        const fetchUrl = selectionUrl(selectedOption, ids, true);

        const oldPath = new URL(currentProductUrl, location.origin).pathname;
        const nextPath = publicUrl.pathname;

        // Combined listing / sibling product: full navigation keeps all
        // product-specific sections, app blocks, media and metadata in sync.
        if (nextPath !== oldPath) {
          location.href = publicUrl.toString();
          return;
        }

        select.setAttribute('aria-busy', 'true');
        pdp.setAttribute('aria-busy', 'true');

        const optionPosition = select.dataset.optionPosition;

        try {
          const response = await fetch(fetchUrl.toString(), {
            headers: { Accept: 'text/html' }
          });

          if (!response.ok) throw new Error('Product option update failed');

          const responseText = await response.text();
          const doc = new DOMParser().parseFromString(responseText, 'text/html');
          const nextPdp = doc.getElementById(pdp.id);

          if (!nextPdp) throw new Error('Updated product section was not returned');

          pdp.replaceWith(nextPdp);
          history.replaceState({}, '', publicUrl.toString());

          initProduct(nextPdp);
          window.Shopify?.PaymentButton?.init?.();

          qs(
            `[data-option-select][data-option-position="${optionPosition}"]`,
            nextPdp
          )?.focus();

          announceSelection(nextPdp);
        } catch (error) {
          
          location.href = publicUrl.toString();
        }
      });
    });

    const desktopBar = qs('[data-product-desktop-bar]', pdp);
    const mainBuyRow = qs('.qx-pdp__buy-row', pdp);

    if (desktopBar && mainBuyRow && 'IntersectionObserver' in window) {
      desktopBar.hidden = true;
      const buyObserver = new IntersectionObserver(entries => {
        desktopBar.hidden = Boolean(entries[0]?.isIntersecting);
      }, { threshold: .15 });
      buyObserver.observe(mainBuyRow);
    }


    const mobileBar = qs('[data-product-mobile-bar]', pdp);
    if (mobileBar && mainBuyRow) {
      mobileBar.hidden = true;
      mobileBar.setAttribute('aria-hidden', 'true');

      const mobileViewport = window.matchMedia('(max-width: 749px)');
      let mainBuyVisible = true;

      const mobileOverlayBlocked = () => (
        document.body.classList.contains('qx-cart-open') ||
        document.body.classList.contains('qx-wishlist-drawer-open') ||
        document.body.classList.contains('qx-quick-view-open') ||
        document.body.classList.contains('qx-search-open') ||
        Boolean(document.querySelector('dialog[open]'))
      );

      const mobileKeyboardOpen = () => (
        window.visualViewport &&
        (window.innerHeight - window.visualViewport.height) > 180
      );

      const syncMobileBar = () => {
        const shouldShow = mobileViewport.matches &&
          !mainBuyVisible &&
          !mobileOverlayBlocked() &&
          !mobileKeyboardOpen();

        mobileBar.hidden = !shouldShow;
        mobileBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        mobileBar.classList.toggle('is-visible', shouldShow);
      };

      const calculateMainVisibility = () => {
        const rect = mainBuyRow.getBoundingClientRect();
        mainBuyVisible = rect.bottom > 0 && rect.top < window.innerHeight;
        syncMobileBar();
      };

      if ('IntersectionObserver' in window) {
        const mobileBuyObserver = new IntersectionObserver(entries => {
          mainBuyVisible = Boolean(entries[0]?.isIntersecting);
          syncMobileBar();
        }, {
          threshold: 0.01,
          rootMargin: '0px 0px -72px 0px'
        });
        mobileBuyObserver.observe(mainBuyRow);
      }

      window.addEventListener('scroll', calculateMainVisibility, { passive: true });
      window.addEventListener('resize', calculateMainVisibility, { passive: true });
      window.visualViewport?.addEventListener('resize', syncMobileBar, { passive: true });
      mobileViewport.addEventListener?.('change', calculateMainVisibility);

      const bodyStateObserver = new MutationObserver(syncMobileBar);
      bodyStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      calculateMainVisibility();
    }

    qsa('[data-pdp-delivery-estimator]', pdp).forEach(estimator => {
      const minDays = Math.max(1, Number(estimator.dataset.minDays || 3));
      const maxDays = Math.max(minDays, Number(estimator.dataset.maxDays || 7));
      const target = qs('[data-delivery-estimate]', estimator);
      if (!target) return;
      const now = new Date(); const minDate = new Date(now); const maxDate = new Date(now);
      minDate.setDate(now.getDate() + minDays); maxDate.setDate(now.getDate() + maxDays);
      const format = date => new Intl.DateTimeFormat(document.documentElement.lang || 'en', {month:'short', day:'numeric'}).format(date);
      target.textContent = `${format(minDate)} – ${format(maxDate)}`;
    });

    const qty = qs('[data-product-qty]', pdp);
    const input = qs('input[name="quantity"]', qty || pdp);

    qs('[data-qty-minus]', qty || pdp)?.addEventListener('click', () => {
      if (input) input.value = Math.max(1, Number(input.value || 1) - 1);
    });

    qs('[data-qty-plus]', qty || pdp)?.addEventListener('click', () => {
      if (input) input.value = Number(input.value || 1) + 1;
    });
  };

  const announce = message => {
    const live = document.querySelector('[data-qx-commerce-live]');
    if (live && message) {
      live.textContent = '';
      requestAnimationFrame(() => { live.textContent = message; });
    }
  };

  const toast = message => {
    window.QXTheme?.showToast?.(message);
    announce(message);
  };

  const initProductActions = () => {
    if (document.documentElement.dataset.qxProductActionsReady === 'true') return;
    document.documentElement.dataset.qxProductActionsReady = 'true';

    document.addEventListener('click', async event => {
      const volume = event.target.closest('[data-qx-volume-qty]');
      if (volume) {
        event.preventDefault();
        const quantity = Math.max(1, Number(volume.dataset.qxVolumeQty) || 1);
        qsa('[name="quantity"],[data-qv-qty-input]').forEach(input => {
          if (input.closest('[data-product-root],.qx-pdp-enhance')) input.value = quantity;
        });
        toast(`Quantity set to ${quantity}`);
        return;
      }

      const coupon = event.target.closest('[data-qx-copy-coupon]');
      if (coupon) {
        event.preventDefault();
        const code = coupon.dataset.qxCopyCoupon || '';
        const label = coupon.querySelector('[data-qx-copy-label]');
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          if (label) {
            const previous = label.textContent;
            label.textContent = 'Copied!';
            setTimeout(() => { label.textContent = previous; }, 1600);
          }
          announce('Coupon code copied');
        } catch (_) {
          toast('Could not copy the coupon code');
        }
        return;
      }

      const questionOpen = event.target.closest('[data-qx-question-open]');
      if (questionOpen) {
        event.preventDefault();
        document.getElementById(questionOpen.dataset.qxQuestionOpen)?.showModal?.();
        return;
      }

      const questionClose = event.target.closest('[data-qx-question-close]');
      if (questionClose) {
        event.preventDefault();
        questionClose.closest('[data-qx-question-dialog]')?.close?.();
        return;
      }

      const fbtAdd = event.target.closest('[data-qx-fbt-add]');
      if (fbtAdd) {
        event.preventDefault();
        const shell = fbtAdd.closest('[data-qx-fbt]');
        const status = shell?.querySelector('[data-qx-fbt-status]');
        const currentId = document.querySelector('[data-product-root] [data-variant-id]')?.value || '';
        const selected = [...(shell?.querySelectorAll('[data-qx-fbt-item]:checked') || [])]
          .map(input => input.value)
          .filter(Boolean);
        const ids = [currentId, ...selected].filter(Boolean);

        if (!ids.length) {
          if (status) status.textContent = 'Choose an available product.';
          return;
        }

        fbtAdd.disabled = true;
        if (status) status.textContent = 'Adding…';

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {'Content-Type':'application/json','Accept':'application/json'},
            body: JSON.stringify({items: ids.map(id => ({id:Number(id),quantity:1}))})
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload?.description || 'Could not add selected products.');
          if (status) status.textContent = 'Added to cart.';
          announce('Selected products added to cart');
          window.QXCartShell?.openCart?.(fbtAdd);
        } catch (error) {
          if (status) status.textContent = error.message || 'Could not add selected products.';
          fbtAdd.disabled = false;
        }
      }
    });
  };

  const boot = scope => {
    const roots = [];
    if (scope?.matches?.('[data-product-root]')) roots.push(scope);
    const closest = scope?.closest?.('[data-product-root]');
    if (closest && !roots.includes(closest)) roots.push(closest);
    scope?.querySelectorAll?.('[data-product-root]').forEach(root => {
      if (!roots.includes(root)) roots.push(root);
    });
    roots.forEach(initProduct);
  };

  initProductActions();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:block:select', event => boot(event.target));
})();
